import React, { useState, useEffect } from 'react';
import { Star, Lock } from 'lucide-react';
import { USER_ROLES } from '../utils/roles';
import AuthModal from './AuthModal';
import { useTranslation } from 'react-i18next';

export default function WatchlistButton({ ticker, user, userId, userRole, size = 'md', className = '', onWatchlistChange }) {
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('warning');
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const { t } = useTranslation();

  const API_URL = process.env.REACT_APP_API_URL || '';

  // Support both user object and userId
  const actualUserId = userId || user?.uid;

  useEffect(() => {
    if (actualUserId && ticker) {
      checkWatchlistStatus();
    }
  }, [actualUserId, ticker]);

  const checkWatchlistStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/watchlist/${actualUserId}/check/${ticker}`);
      const data = await response.json();

      if (data.success) {
        setIsInWatchlist(data.isInWatchlist);
      }
    } catch (error) {
      console.error('Error checking watchlist status:', error);
    }
  };

  const toggleWatchlist = async (e) => {
    e.stopPropagation(); // Prevent event bubbling

    if (!actualUserId) {
      setModalType('warning');
      setModalTitle(t('watchlist.loginRequired') || '로그인 필요');
      setModalMessage(t('watchlist.loginRequiredMessage') || '관심 종목을 추가하려면 로그인이 필요합니다.');
      setShowModal(true);
      return;
    }

    // Check if user is a guest
    if (userRole === USER_ROLES.GUEST) {
      setModalType('warning');
      setModalTitle(t('watchlist.guestRestriction') || '게스트 제한');
      setModalMessage(t('watchlist.guestRestrictionMessage') || '게스트는 관심 종목을 추가할 수 없습니다.\n로그인 후 이용해주세요.');
      setShowModal(true);
      return;
    }

    setLoading(true);

    try {
      if (isInWatchlist) {
        // Remove from watchlist
        const response = await fetch(`${API_URL}/api/watchlist/${actualUserId}/remove/${ticker}`, {
          method: 'DELETE',
        });

        const data = await response.json();
        if (data.success) {
          setIsInWatchlist(false);
          if (onWatchlistChange) {
            onWatchlistChange();
          }
        }
      } else {
        // Add to watchlist
        const response = await fetch(`${API_URL}/api/watchlist/${actualUserId}/add`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ticker }),
        });

        const data = await response.json();
        if (data.success) {
          setIsInWatchlist(true);
          if (onWatchlistChange) {
            onWatchlistChange();
          }
        }
      }
    } catch (error) {
      console.error('Error toggling watchlist:', error);
      setModalType('error');
      setModalTitle(t('watchlist.updateFailed') || '업데이트 실패');
      setModalMessage(t('watchlist.updateFailedMessage') || '관심 종목 업데이트에 실패했습니다.');
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Size configurations
  const sizes = {
    xs: {
      button: 'p-0.5',
      icon: 'w-3 h-3',
      text: 'text-xs',
      iconOnly: true
    },
    sm: {
      button: 'p-1.5',
      icon: 'w-4 h-4',
      text: 'text-xs'
    },
    md: {
      button: 'p-2',
      icon: 'w-5 h-5',
      text: 'text-sm'
    },
    lg: {
      button: 'p-3',
      icon: 'w-6 h-6',
      text: 'text-base'
    }
  };

  const sizeConfig = sizes[size] || sizes.md;

  // For guest users, show a disabled/muted button
  const isGuest = userRole === USER_ROLES.GUEST;

  return (<>
    <button
      onClick={toggleWatchlist}
      disabled={loading}
      className={`
        inline-flex items-center ${sizeConfig.iconOnly ? 'gap-0' : 'gap-2'} rounded-lg
        transition-all duration-200
        ${isInWatchlist
          ? 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100 border border-yellow-200'
          : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
        }
        ${loading ? 'opacity-50 cursor-not-allowed' : isGuest ? 'opacity-60 cursor-pointer' : 'hover:shadow-md'}
        ${sizeConfig.button}
        ${className}
      `}
      title={isGuest ? '로그인 후 이용 가능합니다' : (isInWatchlist ? 'Remove from watchlist' : 'Add to watchlist')}
    >
      <Star
        className={`
          ${sizeConfig.icon}
          ${isInWatchlist ? 'fill-yellow-500 text-yellow-500' : 'text-gray-400'}
          ${loading ? 'animate-pulse' : ''}
        `}
      />
      {!sizeConfig.iconOnly && (
        <span className={`font-medium ${sizeConfig.text}`}>
          {isInWatchlist ? 'Watching' : 'Watch'}
        </span>
      )}
    </button>
    
    {/* Guest/Login/Error Modal */}
    <AuthModal
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      type={modalType}
      title={modalTitle}
      message={modalMessage}
    />
    </>
  );
}
